<template>
  <div class="dpi_container">
    <div class="dpi_industry">{{ project.general?.industry }}</div>
    <dl class="dpi_description">
      <dt>
        <public-link to="/page/policy-risks" class="dpi_link" target="_blank">
          Рейтинг:
        </public-link>
      </dt>
      <dd>
        <public-link
          to="/page/policy-risks"
          class="dpi_link_success"
          target="_blank"
        >
          {{ project.general?.mortgageRank }}
        </public-link>
      </dd>
      <dt>Ставка (годовых):</dt>
      <dd v-if="loan?.interestRateType === InterestRateType.VARIABLE">
        {{
          loan?.interestRate
            ? Math.round(loan.interestRate * 10) / 10 + "%"
            : ""
        }}
        <CTooltip
          text="Прогноз доходности от продажи объекта"
          :text-position="TooltipTextPosition.BOTTOM_RIGHT"
        ></CTooltip>
      </dd>
      <dd v-else>
        {{
          loan?.interestRate
            ? Math.round(loan.interestRate * 10) / 10 + "%"
            : ""
        }}
      </dd>
      <dt>Тип ставки:</dt>
      <dd>
        {{ interestRateTypeLabel }}
      </dd>
      <dt>Срок:</dt>
      <dd>
        {{ loan?.initialTerm != null ? loan.initialTerm + " мес." : "—" }}
      </dd>
      <dt>Комиссия:</dt>
      <dd>
        {{ projectDividendFee ? projectDividendFee : 0 }}%
        <CTooltip
          text="Комиссия за управление залогом"
          :text-position="TooltipTextPosition.BOTTOM_RIGHT"
        ></CTooltip>
      </dd>
      <dt>Тип объекта:</dt>
      <dd>{{ loan?.objectType ?? "—" }}</dd>
      <dt>LTV:</dt>
      <dd>
        {{ loan?.ltv ? Math.round(loan.ltv * 10) / 10 + "%" : "" }}
      </dd>
      <dt>Тип графика:</dt>
      <dd>{{ paymentScheduleType ?? "—" }}</dd>
      <dt>Тип сделки:</dt>
      <dd>{{ project.type === ProjectType.PROJECT ? "Проект" : "Займ" }}</dd>
      <dt>Дата окончания сбора:</dt>
      <dd>
        <date-format
          v-if="loan?.endFundraisingDate"
          :date="loan.endFundraisingDate"
        />
        <template v-else>—</template>
      </dd>
      <dt
        v-if="
          project.status === ProjectStatus.PAID ||
          project.status === ProjectStatus.LOAN_PAYMENT
        "
      >
        Дата выдачи займа:
      </dt>
      <dd
        v-if="
          project.status === ProjectStatus.PAID ||
          project.status === ProjectStatus.LOAN_PAYMENT
        "
      >
        <date-format v-if="loan?.loanIssuedAt" :date="loan.loanIssuedAt" />
        <template v-else>—</template>
      </dd>
      <dt v-if="loan?.buybackDate">Дата откупа:</dt>
      <dd v-if="loan?.buybackDate">
        <date-format :date="loan.buybackDate" />
      </dd>
    </dl>
    <div v-if="isFundraising">
      <form @submit="invest">
        <div class="form-group">
          <div class="dpi_investment_container">
            <input-money
              v-model="investmentAmount"
              @keyup="validateInvestmentAmount"
              @blur="validateInvestmentAmount"
              :min="minInvestment"
              class="dpi_investment_container_input"
              placeholder="Введите сумму"
              required
            />
            <button
              type="submit"
              class="dpi_investment_container_btn"
              :disabled="investDisabled"
            >
              <template v-if="processing">
                <span class="dpi_investment_container_btn__loader">
                  <Loader
                    size="small"
                    :overlay="false"
                    aria-label="Подготовка"
                  />
                </span>
                Подготовка…
              </template>
              <template v-else>Инвестировать</template>
            </button>
          </div>
          <input-errors :errors="allErrors" />
          <div
            v-if="!this.profile.lenderVerified"
            class="small form-text text-danger"
          >
            Для возможности инвестировать необходимо
            <router-link
              :to="{ name: 'authorization' }"
              class="font-weight-bold text-danger"
              >пройти авторизацию</router-link
            >
          </div>
          <div class="form-group mt-2">
            <span class="text-muted">Доступно:</span>
            <money :money="investmentAvailableSum" class="ml-1" />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import DateFormat from "@/components/common/components/DateFormat.vue";
import CTooltip from "@/components/common/components/CTooltip.vue";
import { TooltipTextPosition } from "@/utils/api-client/enums/TooltipTextPosition";
import { InterestRateType } from "@/utils/api-client/enums/InterestRateType";
import Money from "@/components/common/components/Money.vue";
import InputMoney from "@/components/common/components/InputMoney.vue";
import InputErrors from "@/components/common/components/InputErrors.vue";
import {
  Money as ApiMoney,
  PaymentScheduleType,
  ProjectStatus,
  TransactionType,
} from "@/utils/api-client";
import { ProjectType } from "@/utils/api-client/enums/ProjectType";
import { formatAmount } from "@/components/common";
import Loader from "@/components/common/components/Loader.vue";
import PublicLink from "@/components/common/components/PublicLink.vue";
import {
  createFeeSettingsProvider,
  createInvestmentManager,
  createInvestmentManagerV2,
} from "@/utils/di";

export default {
  name: "DetailedProjectInvestment",
  components: {
    InputErrors,
    InputMoney,
    Loader,
    Money,
    CTooltip,
    DateFormat,
    PublicLink,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
    profile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      investmentAmount: null,
      errors: {},
      processing: false,
      feeSettings: null,
    };
  },
  async mounted() {
    const feeSettingsProvider = await createFeeSettingsProvider();
    this.feeSettings = await feeSettingsProvider.getList({
      projectUuids: [this.project.uuid],
    });
  },
  computed: {
    loan() {
      return this.project?.loan;
    },
    interestRateTypeLabel() {
      if (!this.loan?.interestRateType) {
        return "—";
      }
      return this.loan.interestRateType === InterestRateType.FIXED
        ? "Фиксированная"
        : "Переменная";
    },
    ProjectStatus() {
      return ProjectStatus;
    },
    ProjectType() {
      return ProjectType;
    },
    InterestRateType() {
      return InterestRateType;
    },
    TooltipTextPosition() {
      return TooltipTextPosition;
    },
    paymentScheduleType() {
      const paymentScheduleTypes = {
        [PaymentScheduleType.MONTHLY_PAYMENTS]: "Проценты ежемесячно",
        [PaymentScheduleType.QUARTERLY_PAYMENTS]: "Ежеквартальный",
        [PaymentScheduleType.PAYMENT_AT_THE_END]: "Оплата в конце",
      };
      const scheduleType = this.loan?.paymentSchedule?.scheduleType;
      if (scheduleType != null && paymentScheduleTypes[scheduleType]) {
        return paymentScheduleTypes[scheduleType];
      }
      return undefined;
    },
    projectDividendFee() {
      let fee;
      if (this.feeSettings) {
        this.feeSettings.items.map((item) => {
          if (item.transactionType === TransactionType.DIVIDEND_INTEREST_FEE) {
            fee = item.percent;
          }
        });
      }
      return fee;
    },
    isFundraising() {
      return this.project.status === ProjectStatus.FUND_RAISING;
    },
    isRegistration() {
      const loan = this.loan;
      const regSum = loan?.collateralRegistrationSum;
      if (regSum?.amount && loan?.loanSum && loan?.investedSum) {
        return loan.loanSum.amount - loan.investedSum.amount <= regSum.amount;
      }
      return false;
    },
    investmentAvailable() {
      return (
        this.isFundraising &&
        this.investmentAvailableAmount >= this.minInvestment
      );
    },
    balanceEnough() {
      return this.profile.balance.amount >= this.minInvestmentSum.amount;
    },
    minInvestmentSum() {
      const code = this.loan?.loanSum?.currencyCode ?? "RUB";
      return new ApiMoney(this.minInvestment * 100, code);
    },
    minInvestment() {
      const amount = this.loan?.minInvestment?.amount;
      return typeof amount === "number" ? amount / 100 : 0;
    },
    maxInvestmentAllow() {
      return this.profile.maxInvestmentAllow;
    },
    investmentAvailableSum() {
      const loan = this.loan;
      const currency = loan?.loanSum?.currencyCode ?? "RUB";
      if (!loan?.loanSum || !loan.investedSum) {
        return new ApiMoney(0, currency);
      }
      let amount = loan.loanSum.amount - loan.investedSum.amount;
      amount -= loan.totalReservedSum?.amount ?? 0;
      if (loan.lenderReservedSum?.amount != null) {
        amount += loan.lenderReservedSum.amount;
      }

      if (amount > this.profile.balance.amount) {
        amount = this.profile.balance.amount;
      }

      if (this.profile.limitLeft && amount > this.profile.limitLeft.amount) {
        amount = this.profile.limitLeft.amount;
      }

      return new ApiMoney(amount, currency);
    },
    investmentAvailableAmount() {
      return Math.floor(this.investmentAvailableSum.amount / 100);
    },
    limitLeft() {
      return this.profile.limitLeft
        ? Math.floor(this.profile.limitLeft.amount / 100)
        : null;
    },
    requiresNotary() {
      return (
        this.profile.lenderVerified &&
        !this.profile.primaryMarketAllow &&
        !this.loan?.noProxy
      );
    },
    businessErrors() {
      const errors = [];
      const amount = Number(this.investmentAmount || 0);
      if (!amount) {
        errors.push("Введите сумму инвестиции");
      }
      if (this.limitLeft && amount > this.limitLeft) {
        errors.push(
          "Сумма инвестиция превышает остаток доступного лимита инвестирования "
        );
      } else if (amount > this.investmentAvailableAmount) {
        errors.push("Сумма инвестиции превышает доступный остаток");
      }
      if (this.requiresNotary) {
        errors.push("Необходима доверенность");
      }
      if (this.profile.lenderVerified && !this.profile.isRulesConfirmed) {
        errors.push("Ознакомьтесь с правилами платформы");
      }
      if (this.isRegistration && !this.profile.maxInvestmentAllow) {
        errors.push("Минимальная сумма набрана");
      }
      if (this.profile.lenderVerified && !this.profile.investmentAllow) {
        errors.push("Инвестирование недоступно");
      }
      if (
        this.profile.lenderVerified &&
        (this.investmentAvailable === false || this.balanceEnough === false)
      ) {
        if (!this.balanceEnough) {
          errors.push("Недостаточно средств на балансе");
        } else if (this.profile.investmentLimit?.amount === 0) {
          errors.push(
            "Вы достигли лимита для инвестирования в год." +
              " Чтобы увеличить этот лимит обратитесь в службу технической поддержки."
          );
        } else if (this.investmentAvailableAmount < this.minInvestment) {
          errors.push("Минимальная сумма набрана");
        } else {
          errors.push("Недостаточный лимит для покупки");
        }
      }
      return errors;
    },
    allErrors() {
      const rawErrors = [
        ...(this.errors.submit || []),
        ...(this.errors.investmentAmount || []),
        ...this.businessErrors,
      ];

      if (!this.profile.lenderVerified) {
        return [];
      }

      const unique = [...new Set(rawErrors)];

      const hasExceedsAvailable = unique.includes(
        "Сумма инвестиции превышает доступный остаток"
      );
      const hasMinCollected = unique.includes("Минимальная сумма набрана");

      let filtered = unique.filter((message) => {
        if (
          hasExceedsAvailable &&
          message.startsWith("Максимальная сумма инвестиции")
        ) {
          return false;
        }
        return !(
          hasMinCollected &&
          (message.startsWith("Минимальная сумма инвестиции") ||
            message.startsWith("Максимальная сумма инвестиции"))
        );
      });

      const priorityOrder = [
        "Инвестирование недоступно",
        "Необходима доверенность",
        "Ознакомьтесь с правилами платформы",
        "Минимальная сумма набрана",
        "Недостаточно средств на балансе",
        "Вы достигли лимита для инвестирования в год.",
        "Сумма инвестиции превышает доступный остаток",
        "Минимальная сумма инвестиции",
        "Максимальная сумма инвестиции",
        "Введите сумму инвестиции",
      ];

      const priorityIndex = (msg) => {
        const idx = priorityOrder.findIndex((p) => msg.startsWith(p));
        return idx === -1 ? priorityOrder.length : idx;
      };

      filtered.sort((a, b) => priorityIndex(a) - priorityIndex(b));

      return filtered;
    },
    investDisabled() {
      if (this.processing || !this.profile.lenderVerified) {
        return true;
      }
      const submitErrors = this.errors.submit || [];
      const blockingErrors = this.allErrors.filter(
        (msg) => !submitErrors.includes(msg)
      );
      return blockingErrors.length > 0;
    },
  },
  methods: {
    validateInvestmentAmount() {
      this.errors.submit = [];
      const errors = [];
      const amount = Number(this.investmentAmount || 0);

      if (amount && amount < this.minInvestment) {
        errors.push(
          `Минимальная сумма инвестиции ${formatAmount(
            this.minInvestment
          )} руб.`
        );
      }

      if (amount && amount > this.investmentAvailableAmount) {
        if (this.investmentAvailableAmount <= 0) {
          errors.push("Доступный остаток для инвестиций равен нулю");
        } else {
          errors.push(
            `Максимальная сумма инвестиции ${formatAmount(
              this.investmentAvailableAmount
            )} руб.`
          );
        }
      }

      this.errors.investmentAmount = errors;
      return errors.length === 0;
    },
    async invest(e) {
      e.preventDefault();
      if (this.investDisabled) return;
      this.errors.submit = [];
      this.processing = true;
      try {
        const manager = await createInvestmentManager();
        const result = await manager.createConfirmation(
          this.project.uuid,
          new ApiMoney(
            this.investmentAmount * 100,
            this.profile.balance.currencyCode
          )
        );
        const managerV2 = await createInvestmentManagerV2();
        await managerV2.getConfirmation(result.uuid);
        await this.$router.push({
          name: "signDocuments",
          params: { confirmationUuid: result.uuid },
        });
      } catch {
        this.errors.submit = ["Произошла ошибка. Повторите ещё раз."];
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dpi_container {
  display: flex;
  flex-direction: column;
  border-radius: 0.8rem;
  padding: 2rem;
  background: var(--white);
  height: 100%;
}

.dpi_industry {
  color: var(--secondary-gray);
  margin-bottom: 1rem;
}

.dpi_description {
  font-weight: 400;
  font-size: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  align-items: start;
  align-content: start;
  justify-content: start;
  display: grid;
  grid-template-columns: 40% 56%;
  column-gap: 1rem;
  row-gap: 1rem;

  dt,
  dd {
    margin: 0;
    line-height: 1.5;
    max-width: 100%;
    align-self: end;
    padding: 0;
  }

  dt {
    color: var(--secondary-gray);
    grid-column: 1;
  }

  dd {
    color: var(--dark);
    grid-column: 2;
    text-align: right;
  }
}

.dpi_investment_container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  position: relative;

  &_input {
    border: 1px solid var(--stroke-on-white-color);
    padding: 1rem;
    border-radius: 0.8rem;
    width: 100%;
    outline: none;
  }

  &::after {
    content: "₽";
    position: absolute;
    right: 15px;
    top: 22%;
    transform: translateY(-50%);
    color: var(--secondary-gray);
    pointer-events: none;
    font-size: 16px;
    font-weight: 400;
  }

  &_btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background-color: var(--primary-green-color);
    cursor: pointer;
    color: var(--white);
    width: 100%;
    border: none;
    padding: 1.2rem;
    border-radius: 0.8rem;

    &:disabled {
      background-color: var(--stroke-on-white-color);
      cursor: not-allowed;
    }

    &__loader {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }
}

:deep(.dpi_investment_container_btn__loader) {
  .loader__img {
    filter: brightness(0) invert(1);
  }
}
.dpi_link {
  color: var(--secondary-gray);
  text-decoration: none;
}
.dpi_link_success {
  color: var(--primary-green-color);
  font-size: 1.3rem;
}

.warning-img {
  width: 2.5rem;
}

.dpi_border_success {
  border-color: var(--primary-green-color);
}

.block-invest {
  .form-group {
    width: 25rem;
    max-width: 100%;
  }
}
</style>
